import { isVersionGreaterThanOther } from "@/utils/SemanticVersionCodeHelper";
import { ComBox, isHyDryMiniSerial } from "./ComBox";

/**
 * Enumerates device capabilities that were previously gated by firmware version checks.
 *
 * HyDry Mini (IPU generation K) supports all data/protocol capabilities from day one,
 * even though its firmware version starts at 0.1.0. Standard ComBoxes require specific
 * firmware versions to unlock these capabilities.
 *
 * The HAS_DISPLAY capability is hardware-dependent: standard ComBoxes always have a display,
 * while Minis never do.
 */
export enum DeviceCapability {
  /** Extended sensor support (8 external + 1 room). ComBox: >= 4.0.0 */
  EXTENDED_SENSORS = "EXTENDED_SENSORS",

  /** Sensor usage bit-field encoding. ComBox: >= 0.12.6 */
  SENSOR_USAGE_INFO = "SENSOR_USAGE_INFO",

  /** Drying config name format (8-byte string). ComBox: >= 0.13.7 */
  DRYING_CONFIG_NAME = "DRYING_CONFIG_NAME",

  /** Room drying support. ComBox: >= 1.3.13 */
  ROOM_DRYING = "ROOM_DRYING",

  /** Device has a physical display. ComBox: always. Mini: never. */
  HAS_DISPLAY = "HAS_DISPLAY",
}

/**
 * Minimum firmware version required for each capability on standard ComBoxes.
 * Mini devices have all capabilities (except HAS_DISPLAY) regardless of firmware version.
 */
const COMBOX_CAPABILITY_VERSIONS: Record<DeviceCapability, string | null> = {
  [DeviceCapability.EXTENDED_SENSORS]: "4.0.0",
  [DeviceCapability.SENSOR_USAGE_INFO]: "0.12.6",
  [DeviceCapability.DRYING_CONFIG_NAME]: "0.13.7",
  [DeviceCapability.ROOM_DRYING]: "1.3.13",
  [DeviceCapability.HAS_DISPLAY]: null, // Always available on standard ComBoxes
};

/**
 * Check whether a device has a given capability, using raw serial number + firmware version.
 * Use this in contexts where a full ComBox object is not available (e.g., parsing, commands).
 */
export function hasCapabilityForVersion(
  serialNumber: string,
  firmwareVersion: string,
  capability: DeviceCapability,
  isMiniOverride?: boolean,
): boolean {
  const isMini = isMiniOverride ?? isHyDryMiniSerial(serialNumber);

  if (capability === DeviceCapability.HAS_DISPLAY) {
    // Mini devices never have a display; standard ComBoxes always do
    return !isMini;
  }

  if (isMini) {
    // Mini (IPU generation K) supports all data/protocol capabilities from day one
    return true;
  }

  // Standard ComBox: check firmware version threshold
  const requiredVersion = COMBOX_CAPABILITY_VERSIONS[capability];
  if (requiredVersion === null) {
    return true;
  }

  return isVersionGreaterThanOther(firmwareVersion, requiredVersion, true);
}

/**
 * Check whether a device has a given capability, using the ComBox object.
 */
export function hasCapability(comBox: ComBox, capability: DeviceCapability): boolean {
  return hasCapabilityForVersion(
    comBox.serialNo,
    comBox.comBoxFirmwareVersion,
    capability
  );
}
